export interface VariableDefinition {
    key: string;
    label: string;
    description: string;
}

export type VariableTemplateToken =
    | { kind: "text"; value: string }
    | { kind: "variable"; key: string };

/**
 * Shared by every consumer of a stored `{{token}}` string so brace-escaping and
 * variable detection can't drift between them. A `{{...}}` whose key matches
 * `\w+` is a variable slot (whether the key is known is the consumer's concern).
 */
export function tokenizeVariableTemplate(
    template: string,
): VariableTemplateToken[] {
    const tokens: VariableTemplateToken[] = [];
    let text = "";
    let index = 0;

    const flushText = () => {
        if (text.length > 0) {
            tokens.push({ kind: "text", value: text });
            text = "";
        }
    };

    while (index < template.length) {
        const char = template[index];

        if (char === "\\" && index + 1 < template.length) {
            text += template[index + 1];
            index += 2;
            continue;
        }

        if (char === "{" && template[index + 1] === "{") {
            const closeIndex = template.indexOf("}}", index + 2);
            const key =
                closeIndex === -1
                    ? null
                    : template.slice(index + 2, closeIndex);
            if (key !== null && /^\w+$/.test(key)) {
                flushText();
                tokens.push({ kind: "variable", key });
                index = closeIndex + 2;
                continue;
            }
        }

        text += char;
        index += 1;
    }

    flushText();
    return tokens;
}

export type VariableTextSegment =
    | { kind: "text"; value: string }
    | { kind: "variable"; variableKey: string };

/** Only keys accepted by `isKnownKey` become variables; unknown `{{...}}` stays literal. */
export function parseTokenString(
    value: string,
    isKnownKey: (key: string) => boolean,
): VariableTextSegment[] {
    const segments: VariableTextSegment[] = [];
    let text = "";

    const flushText = () => {
        if (text.length > 0) {
            segments.push({ kind: "text", value: text });
            text = "";
        }
    };

    for (const token of tokenizeVariableTemplate(value)) {
        if (token.kind === "text") {
            text += token.value;
            continue;
        }

        if (isKnownKey(token.key)) {
            flushText();
            segments.push({ kind: "variable", variableKey: token.key });
        } else {
            text += `{{${token.key}}}`;
        }
    }

    flushText();
    return segments;
}
